import { z } from 'zod'
import { Brand } from '../../shared'
import { Identifier } from '../../shared/types'
import { ReferenceType } from './types'

export const BrandSchema = z.nativeEnum(Brand).default(Brand.LYKA)

export const CreatePaymentIntent = z.object({
  // Make this optional to avoid a breaking change in API
  brand: BrandSchema,
  amount: z.number().int().nonnegative(),
  email: z.string().optional(),
  name: z.string().optional(),
  paymentMethodConfigId: z.string().optional(),
  description: z.string().optional(),
})

export const CreateSetupIntent = z.object({
  brand: BrandSchema,
  customerId: z.string().optional(),
  paymentMethodConfigId: z.string().optional(),
})

export const UpdatePaymentIntent = z.object({
  brand: BrandSchema,
  customerId: z.string().optional(),
})

export const CreatePaymentReference = z.object({
  referenceId: z.string().nonempty(),
  referenceType: z.nativeEnum(ReferenceType),
})

export const CreateTestConfirmationToken = z.object({
  paymentMethodId: z.string().nonempty(),
  brand: z.nativeEnum(Brand),
})

export type CreatePaymentRefundPayload = z.input<typeof CreatePaymentRefund>
export type CreatePaymentRefundBody = z.output<typeof CreatePaymentRefund>

export type CreatePaymentIntentPayload = z.input<typeof CreatePaymentIntent>
export type CreatePaymentIntentBody = z.output<typeof CreatePaymentIntent>

export type CreateSetupIntentPayload = z.input<typeof CreateSetupIntent>
export type CreateSetupIntentBody = z.output<typeof CreateSetupIntent>

export type UpdatePaymentIntentPayload = z.input<typeof UpdatePaymentIntent>
export type UpdatePaymentIntentBody = z.output<typeof UpdatePaymentIntent>

export type CreateTestConfirmationTokenPayload = z.input<typeof CreateTestConfirmationToken>
export type CreateTestConfirmationTokenBody = z.output<typeof CreateTestConfirmationToken>

export interface PaymentIntentEntity {
  id: string
  amount: number
  paymentMethodId?: string
  customerId?: string
  currency: 'aud'
  clientSecret?: string | null
  description?: string
  status: 'requires_payment_method' | 'requires_confirmation' | 'requires_action' | 'processing' | 'requires_capture' | 'canceled' | 'succeeded'
}

export interface SetupIntentEntity {
  id: string
  paymentMethodId?: string
  customerId?: string
  clientSecret?: string
  status: string
}

export interface PaymentMethodEntity {
  id: string
  provider: 'stripe'
  providerPaymentMethodId?: string
}

export interface CustomerSourceEntity {
  id: string
}

export interface ConfirmationTokenEntity {
  id: string
}

export interface CustomerEntity {
  id: string
  brand: Brand
  payments?: PaymentEntity[]
  paymentMethods?: PaymentMethodEntity[]
  defaultPaymentMethod?: PaymentMethodEntity
}

export interface PaymentReferenceEntity {
  id: string
  referenceId: string
  referenceType: string
  refundId?: string | null
}

export interface PaymentEntity {
  id: string
  providerPaymentId: string | null
  amount: number
  status: 'complete' | 'failed'
  provider: 'stripe'
  customer?: CustomerEntity
  paymentMethod?: PaymentMethodEntity
  paymentReferences?: PaymentReferenceEntity[]
  refunds?: RefundEntity[]
}

export const ChargeCustomer = z.object({
  brand: BrandSchema,
  amount: z.number().int().nonnegative(),
  paymentMethodId: z.string().optional(),
  description: z.string().optional(),
  referenceId: z.string().nonempty(),
  referenceType: z.nativeEnum(ReferenceType),
  // Per-charge-attempt Stripe idempotency key, supplied by the caller (orchestration passes
  // `${workflowId}:${orderId}`). MUST be distinct per intended charge — initial vs each
  // dunning retry vs manual/adjustment — so legitimate same-day re-charges are NOT deduped
  // (a same-order key would be replayed/conflict for 24h), while a true retry of the SAME
  // attempt (same key) still dedupes a lost ACK. See docs/payment-charge-idempotency-2026-06-12.md.
  idempotencyKey: z.string().optional(),
})

export type ChargeCustomerPayload = z.input<typeof ChargeCustomer>
export type ChargeCustomerBody = z.output<typeof ChargeCustomer>

export const CapturePayment = z.object({
  brand: BrandSchema,
  userId: Identifier,
  description: z.string().optional(),
  referenceId: z.string(),
  referenceType: z.nativeEnum(ReferenceType),
  customer: z.object({
    email: z.string().email(),
    name: z.string(),
  }).optional(),
  provider: z.object({
    name: z.literal('stripe'),
    paymentIntentId: z.string(),
  }),
  expectedAmount: z.number().int().nonnegative().optional(),
})

export type CapturePaymentPayload = z.input<typeof CapturePayment>
export type CapturePaymentBody = z.output<typeof CapturePayment>

export const ChargeConfirmationToken = z.object({
  brand: z.nativeEnum(Brand),
  userId: Identifier,
  description: z.string().optional(),
  referenceId: z.string(),
  referenceType: z.nativeEnum(ReferenceType),
  customer: z.object({
    email: z.string().email(),
    name: z.string().optional(),
  }),
  provider: z.object({
    name: z.literal('stripe'),
    confirmationTokenId: z.string(),
  }),
  amount: z.number().int().nonnegative(),
})

export type ChargeConfirmationTokenPayload = z.input<typeof ChargeConfirmationToken>
export type ChargeConfirmationTokenBody = z.output<typeof ChargeConfirmationToken>

export interface StripePaymentMethodEntity {
  id: string
  providerCustomerId: string
  brand?: string
  default?: boolean
  expiryMonth?: number
  expiryYear?: number
  last4Digits?: string
  createdAt?: Date
}

export const CreatePaymentRefund = z.object({
  amount: z.number().min(1, 'Amount must be greater than 0'),
  description: z.string().optional().describe('Description of the refund'),
  providerRefundId: z.string().nullable().optional().describe('ID of the payment method for the refund. If the payment method is provided then it will skip provider (eg. Stripe) processing'),
  createdBy: z.string().nonempty().optional().describe('ID of the user who issued the refund'),
  references: z.array(CreatePaymentReference).optional().describe('References associated with the refund'),
}).strict()

export type RefundEntity = z.infer<typeof CreatePaymentRefund> & {
  id: string
  status: 'complete' | 'failed' | 'cancelled' | 'pending'
  createdAt?: Date
}

export interface PaginatedResults<T> {
  data: T[]
  total: number
  count: number
  offset: number
}

export const MigrateCustomerSchema = z.object({
  userId: Identifier,
  providerCustomerId: z.string().optional(),
  isCustomerOnChargeApi: z.boolean().default(false),
})

export type MigrateCustomer = z.infer<typeof MigrateCustomerSchema>

export const MigrateRefundSchema = z.object({
  status: z.literal('complete'),
  id: z.string().uuid(),
  createdBy: z.string().uuid().nullable(),
  amount: z.number(),
  providerRefundId: z.string().optional().nullable(),
})

export type MigrateRefund = z.infer<typeof MigrateRefundSchema>

export const MigratePaymentReferenceSchema = z.object({
  referenceId: z.string(),
  referenceType: z.nativeEnum(ReferenceType),
})

export type MigratePaymentReferences = z.infer<typeof MigratePaymentReferenceSchema>

export const MigratePaymentSchema = z.object({
  id: z.string().uuid(),
  userId: Identifier,
  createdAt: z.coerce.date(),
  paymentReferences: MigratePaymentReferenceSchema.array().default([]),
  providerPaymentMethodId: z.string().optional().nullable(),
  status: z.enum(['complete', 'failed']),
  providerPaymentId: z.string().nullable(),
  amount: z.number(),
  description: z.string().optional(),
  refunds: MigrateRefundSchema.array().default([]),
})

export type MigratePayment = z.infer<typeof MigratePaymentSchema>

export const MigrateCustomerAndPayments = z.object({
  customer: MigrateCustomerSchema,
  payments: MigratePaymentSchema.array(),
})

export type MigrateCustomerAndPayments = z.infer<typeof MigrateCustomerAndPayments>

export const MigrateCustomerAndPaymentsMismatchSchema = z.object({
  path: z.string(),
  expected: z.string().nullable(),
  actual: z.string().nullable(),
})

export type MigrateCustomerAndPaymentsMismatch = z.infer<typeof MigrateCustomerAndPaymentsMismatchSchema>

export const MigrationSuccessResponseSchema = z.object({
  success: z.boolean(),
})

export type MigrationSuccessResponse = z.infer<typeof MigrationSuccessResponseSchema>

export const VerifyMigrateCustomerAndPaymentsResponseSchema = MigrationSuccessResponseSchema.extend({
  userId: z.string().uuid(),
  mismatches: MigrateCustomerAndPaymentsMismatchSchema.array(),
})

export type VerifyMigrateCustomerAndPaymentsResponse = z.infer<typeof VerifyMigrateCustomerAndPaymentsResponseSchema>

export const PaymentRequiredResponseSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('card_error'),
    code: z.string().optional(),
    message: z.string(),
  }),
  z.object({
    kind: z.literal('requires_action'),
    message: z.string(),
    clientSecret: z.string(),
  }),
])

export type PaymentRequiredResponse = z.infer<typeof PaymentRequiredResponseSchema>

/**
 * Error-fidelity payload for a failed Stripe API call surfaced at its TRUE
 * outbound status (e.g. 429 `rate_limit_error`) instead of a flattened generic
 * 500. `type` is Stripe's own `error.rawType`, so throttles/infra failures are
 * countable and distinguishable rather than masked. See
 * docs/hermetic-stripe-testing-design (step 3 — error fidelity).
 */
export const StripeErrorResponseSchema = z.object({
  kind: z.literal('stripe_error'),
  provider: z.literal('stripe'),
  type: z.string(),
  code: z.string().optional(),
  message: z.string(),
  requestId: z.string().optional(),
})

export type StripeErrorResponse = z.infer<typeof StripeErrorResponseSchema>
